// Where to go after signing in or choosing a password: a same-origin path only, never back to the
// sign-in or password screen. Browsers treat `//host` and `/\host` as protocol-relative URLs.
// `safeReturn` is the other kind: an address in one of the other Fieldpiece apps, followed only
// when the account may open that app.

import type { AppLink } from '@fpa/shared';

export function safeRedirect(target: string | undefined): string {
  if (
    !target?.startsWith('/') ||
    /^\/[/\\]/.test(target) ||
    target.startsWith('/login') ||
    target.startsWith('/password')
  )
    return '/';
  return target;
}

/**
 * The address to come back to after signing in. The router's own `href` is its reading of the
 * address written back out: a filter that reads as a number loses its spelling there
 * (`?partner=1.0` comes back as `?partner=1`). Where the browser is at the page that is being
 * opened, its own address is taken as it was typed; otherwise (a navigation inside the app, whose
 * addresses the app wrote itself) the router's.
 */
export function addressToReturnTo(
  routerHref: string,
  pathname: string,
  browser: Pick<Location, 'pathname' | 'search' | 'hash'> | undefined = typeof window !==
  'undefined'
    ? window.location
    : undefined,
): string {
  if (browser === undefined) return routerHref;
  const same = (path: string) => path.replace(/\/+$/, '') || '/';
  if (same(browser.pathname) !== same(pathname)) return routerHref;
  return browser.pathname + browser.search + browser.hash;
}

/**
 * The address in another app that a sign-in may return to (`?return=`): an `http` or `https`
 * address whose origin is one of the apps this account may open (`me.apps`). Anything else gives
 * `null` and the caller lands on Home: another site, a `javascript:` address, a path, nonsense.
 * The origin is read by the URL parser, so `https://counter.example@evil.test` is `evil.test`.
 */
export function safeReturn(
  target: string | undefined,
  apps: readonly Pick<AppLink, 'url'>[] | undefined,
): string | null {
  if (!target) return null;
  let wanted: URL;
  try {
    wanted = new URL(target);
  } catch {
    return null;
  }
  if (wanted.protocol !== 'https:' && wanted.protocol !== 'http:') return null;
  const allowed = (apps ?? []).some((app) => {
    try {
      return new URL(app.url).origin === wanted.origin;
    } catch {
      return false;
    }
  });
  return allowed ? wanted.href : null;
}
